// Vexo analytics — tenant-admin helper.
//
// Single source of truth for: identify, base props on every custom event,
// kill switch checks. Designed so call sites read like
// `trackOrderSubmitted({ order_id, shifts_required, ... })` without needing
// to know about the SDK or auth shape.
//
// Event names + property shapes match the GW Custom Events Tracking Spec —
// 🏢 Client Events.csv. Names are dashboard-keyed and renames break
// continuity, so changes here must be reflected on the Vexo dashboard.

import { currentAdminVar, currentAgencyVar } from '@/util/apollo/cache';
import { enqueueAnalyticsEvent, flushAnalytics } from '@/util/analyticsQueue';

declare global {
  interface Window {
    vexo?: {
      customEvent: (name: string, props: Record<string, unknown>) => void;
      identifyDevice: (
        userId: string | null,
        traits?: Record<string, unknown>,
      ) => void;
      // Optional — present in newer Vexo SDK builds. Called from
      // `flushVexoEvents()` on visibilitychange/pagehide so batched events
      // get sent before the tab closes.
      flush?: () => void;
      // Optional — present if Vexo's web SDK supports post-load init with
      // a project key. `useVexoBootstrap` calls this via `script.onload`
      // when a key is configured for the current env. If Vexo's CDN
      // actually expects a query-param or data-attribute pattern instead,
      // this stays a no-op and the script falls back to domain-based
      // project routing.
      init?: (apiKey: string) => void;
    };
    Cypress?: unknown;
    // Test seam: populated only under Cypress (see `track`).
    __VEXO_TEST_EVENTS__?: Array<{
      name: string;
      props: Record<string, unknown>;
    }>;
  }
}

// Best-effort drain of any events the SDK has queued but not yet shipped.
// Defensive: older SDK builds don't expose `flush`; in that case this is a
// no-op and we rely on whatever flush behavior the SDK does internally.
// Public so the unload listener in `useVexoBootstrap` can call it and call
// sites that perform a hard navigation (logout, agency switch) can call it
// too if they want belt-and-suspenders.
export function flushVexoEvents(): void {
  if (typeof window === 'undefined' || !window.vexo) return;
  try {
    window.vexo.flush?.();
  } catch {
    /* swallow — tracking must never block navigation */
  }
}

// Per-tab session id. Regenerated on cold start (page reload). Used as a
// base prop on every custom event so dashboards can group by session even
// when Vexo's own session boundary doesn't line up with what we mean.
let sessionId: string | null = null;
function getSessionId(): string {
  if (!sessionId) {
    sessionId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
  return sessionId;
}

// Per-flow stores — reset together at the top of trackOrderCreationStarted
// (real new flow) and in __resetVexoSessionForTests (test isolation).
let orderFlowStartMs: number | null = null;
let orderFlowClientId: string | null = null;
let orderFlowPayRateEditCount = 0;

// Visible for tests.
export function __resetVexoSessionForTests(): void {
  sessionId = null;
  orderFlowStartMs = null;
  orderFlowClientId = null;
  orderFlowPayRateEditCount = 0;
}

function isReady(): boolean {
  return typeof window !== 'undefined' && !!window.vexo;
}

// All id fields ship as strings on the wire. Vexo dashboards aggregate by
// exact type, so `order_id: 1` and `order_id: '1'` would otherwise count
// as two different orders. Callers can pass numbers OR strings — we
// normalize at the helper boundary.
function toIdString(id: string | number | null | undefined): string | null {
  if (id == null) return null;
  return String(id);
}

// `account_id` resolution differs by admin type:
//   - CustomerAdmin  → admin.customer.id     (the customer/account record)
//   - TenantAdmin    → currentAgencyVar()?.id  (agency they're scoped into; null until they enter one)
//   - HelpdeskAdmin  → same as TenantAdmin
type AdminVar = ReturnType<typeof currentAdminVar>;
function resolveAccountId(admin: AdminVar): string | null {
  if (admin && admin.__typename === 'CustomerAdmin') {
    return admin.customer?.id ?? null;
  }
  return currentAgencyVar()?.id ?? null;
}

export function setOrderFlowClientId(
  id: string | number | null | undefined,
): void {
  orderFlowClientId = toIdString(id ?? null);
}

function clientIdFromAdmin(admin: AdminVar): string | null {
  return admin?.__typename === 'CustomerAdmin'
    ? toIdString(admin.customer?.id ?? null) // Client Admin's own customer
    : null;
}

// Best-available geography at order-entry time. We mirror `order_submitted`'s
// `region` semantics (US state) so the started→submitted funnel compares on the
// same dimension. A Client Admin reads it from their default billing account
// (falling back to the first account); a Tenant Admin has no account selected
// at the entry click, so it stays null until a location is chosen downstream.
function regionFromAdmin(admin: AdminVar): string | null {
  if (!admin || admin.__typename !== 'CustomerAdmin') return null;
  const accounts = admin.customer?.accounts ?? [];
  const account = accounts.find((a) => a.default) ?? accounts[0];
  return account?.state ?? null;
}

// account_type distinguishes who initiated the order: a customer's own admin
// (client_admin) vs an agency/helpdesk operator acting on their behalf
// (tenant_admin).
function accountTypeFromAdmin(
  admin: AdminVar,
): 'client_admin' | 'tenant_admin' {
  return admin?.__typename === 'CustomerAdmin'
    ? 'client_admin'
    : 'tenant_admin';
}

// Read at event-fire time so a mid-session agency switch reflects in the
// next event without re-identify. Worth keeping a side note: identifyDevice
// is *not* called on agency switch by default — see §2.2 step 3 for the
// explicit re-identify wiring.
function baseProps(): Record<string, unknown> {
  const admin = currentAdminVar();
  return {
    user_id: admin?.user?.id ?? null,
    admin_id: admin?.id ?? null,
    account_id: resolveAccountId(admin),
    client_id: orderFlowClientId ?? clientIdFromAdmin(admin), // explicit flow client wins
    role: admin?.user?.roleId ?? null,
    actor_type: admin?.__typename ?? null,
    session_id: getSessionId(),
  };
}

export function identifyAdmin(): void {
  if (!isReady()) return;

  const admin = currentAdminVar();
  // Vexo's documented identifier is an email. Skip if missing — leave the
  // session under the device UUID rather than send a numeric id that the
  // dashboard can't render meaningfully.
  const email = admin?.user?.email;
  if (!email) return;

  try {
    window.vexo!.identifyDevice(email, {
      user_id: admin.user.id,
      admin_id: admin.id,
      account_id: resolveAccountId(admin),
      role: admin.user.roleId,
      actor_type: admin.__typename,
    });
  } catch {
    // Tracking must never break the auth flow.
  }
}

// Synchronous teardown of the session→user link. Called from logout and
// clearCache *before* any hard navigation / cache reset.
//
// Order matters: flush BEFORE identifyDevice(null) so any queued events
// (e.g. a `client_logout` fired in the same tick) are shipped under the
// real identity, not anonymously. The visibilitychange/pagehide listeners
// would catch this eventually, but if the caller does a hard navigation
// (window.location.assign / router.push to login), those listeners may
// not have time to drain.
export function unidentify(): void {
  // In-house flush is unconditional — identity-smear protection must not
  // depend on the Vexo SDK ever having loaded. Once Vexo is removed,
  // isReady() will always be false, which would make this permanently dead
  // if it stayed behind that gate. Keep it ahead of the isReady() early
  // return below.
  //
  // { unload: true } is deliberate here, not just at real page-unload: the
  // non-unload path steals the batch synchronously but ships it through the
  // async axios client, which reads the auth token from the request
  // interceptor at send time — AFTER this function returns and the caller
  // clears the token / navigates. The keepalive path reads the token
  // synchronously up front and survives the teardown.
  try {
    flushAnalytics({ unload: true });
  } catch {
    /* swallow */
  }

  if (!isReady()) return;
  flushVexoEvents();
  try {
    window.vexo!.identifyDevice(null);
  } catch {
    /* swallow */
  }
}

// Internal — exported only so the §3 page-view hook and §4 typed wrappers
// can call it. Public consumers should use the typed wrappers, not `track`.
export function track(name: string, props: Record<string, unknown> = {}): void {
  const payload = { ...baseProps(), ...props };

  // Test seam: under Cypress, mirror every event to a window buffer so E2E
  // specs can assert on what would be sent, independent of whether the Vexo
  // SDK script actually loaded. No-op in real usage; runs before isReady().
  if (typeof window !== 'undefined' && window.Cypress) {
    if (!window.__VEXO_TEST_EVENTS__) window.__VEXO_TEST_EVENTS__ = [];
    window.__VEXO_TEST_EVENTS__.push({ name, props: payload });
  }

  // In-house pipeline (dual-write). Deliberately before the isReady() gate:
  // vexo failing to load must not silence the first-party copy.
  try {
    enqueueAnalyticsEvent(name, payload);
  } catch {
    /* swallow */
  }

  if (!isReady()) return;

  try {
    window.vexo!.customEvent(name, payload);
  } catch {
    /* swallow */
  }
}

// ─── Public typed events ─────────────────────────────────────────────────
//
// All event names + property shapes follow GW Custom Events Tracking Spec
// — 🏢 Client Events.csv. Owner column "Backend / Ops" or "System" events
// (#10 order_fill_milestone_reached, #11 order_critically_unfilled_flagged,
// #13 order_completed, #21 client_churned_flagged) are intentionally
// omitted — they fire from the backend, not from this admin app.

// ─── Auth & Account (#1–3) ───────────────────────────────────────────────

export type LoginMethod = 'email' | 'sso' | 'google';
export type AccountType = 'business' | 'enterprise';
export type CompanySizeTier = 'small' | 'mid' | 'large';

export function trackClientLoginSuccess(args: {
  login_method: LoginMethod;
  account_type: AccountType | null;
  company_size_tier: CompanySizeTier | null;
}): void {
  track('client_login_success', {
    login_method: args.login_method,
    account_type: args.account_type,
    company_size_tier: args.company_size_tier,
  });
}

export type SignupSource = 'sales' | 'self-serve' | 'referral';

export function trackClientSignupCompleted(args: {
  company_name: string | null;
  industry: string | null;
  region: string | null;
  signup_source: SignupSource;
}): void {
  // `company_name` is allowed per the spec (it's the company-level identifier
  // surfaced in admin dashboards). `industry` / `region` are coarse segments.
  track('client_signup_completed', {
    company_name: args.company_name,
    industry: args.industry,
    region: args.region,
    signup_source: args.signup_source,
  });
}

export type ProfileUpdateType = 'manual' | 'prompted';

export function trackClientProfileUpdated(args: {
  fields_updated: string[];
  update_type: ProfileUpdateType;
}): void {
  // `fields_updated` carries field NAMES, never values, so PII can't leak.
  track('client_profile_updated', {
    fields_updated: args.fields_updated,
    update_type: args.update_type,
  });
}

// ─── Order Creation & Management (#4–9, #12, order_reposted) ─────────────

export type OrderType = 'GIG' | 'LTA';

export function getOrderFlowStartMs(): number | null {
  return orderFlowStartMs;
}

export function trackOrderCreationStarted(args: {
  order_type: OrderType;
}): void {
  // Flow starts at the entry click — reset all per-flow stores. Stamp the
  // clock BEFORE track()'s kill-switch gate so the shared clock is consistent
  // whether or not the event is actually emitted.
  orderFlowStartMs = Date.now();
  orderFlowPayRateEditCount = 0;
  const admin = currentAdminVar();
  // Client Admin → own customer; Tenant Admin → null until a billing account
  // is picked (Part 4). client_id itself is injected by baseProps().
  setOrderFlowClientId(clientIdFromAdmin(admin));
  track('order_creation_started', {
    order_type: args.order_type,
    region: regionFromAdmin(admin), // Client Admin → default account's state
    account_type: accountTypeFromAdmin(admin), // client_admin vs tenant_admin
  });
}

// Fired the first time a user lands on the order-v2 entry screen (once per user,
// gated by the caller). Distinct from order_creation_started, which fires on the
// Start-order click, not on landing.
export function trackOrderV2FirstEntry(args: { order_type: OrderType }): void {
  track('order_v2_first_entry', { order_type: args.order_type });
}

// Each wizard step has its own event name per the spec
// (`order_creation_step_viewed_1` … `_6` plus `_3b` for the invites
// sub-step). step_index is 1-based to match the spec exactly. step_name is
// the literal display label from GRAVY-9996's key-properties table (see
// STEP_VIEWED_NAME_LABEL) — the string Product filters/segments the Vexo
// dashboards on, e.g. job_type → "Job type", invites → "invites".
export type OrderCreationStepKey =
  | 'job_type'
  | 'schedule'
  | 'staffing'
  | 'invites'
  | 'instructions'
  | 'pay_rate'
  | 'summary';

const STEP_VIEWED_EVENT_NAME: Record<OrderCreationStepKey, string> = {
  job_type: 'order_creation_step_viewed_1',
  schedule: 'order_creation_step_viewed_2',
  staffing: 'order_creation_step_viewed_3',
  invites: 'order_creation_step_viewed_3b',
  instructions: 'order_creation_step_viewed_4',
  pay_rate: 'order_creation_step_viewed_5',
  summary: 'order_creation_step_viewed_6',
};

// step_name labels for the *_step_viewed_* events. These are the literal
// display labels from GRAVY-9996's key-properties table (the strings Product
// filters Vexo dashboards on), which is why the casing/spacing is mixed
// ("Job type" and "pay rate" carry capitals/spaces while "schedule" is bare
// lowercase) — that matches the spec verbatim, not an internal convention.
// The invites sub-step reports its OWN "invites" label so viewed_3b stays
// distinguishable from viewed_3 ("staffing") in dashboard filters.
//
// NOTE: deliberately NOT shared with order_creation_step_completed — that
// event's step_name taxonomy is owned by GRAVY-10183 (open product
// question), so correcting these labels must not change step_completed. See
// STEP_COMPLETED_NAME_LABEL below.
const STEP_VIEWED_NAME_LABEL: Record<OrderCreationStepKey, string> = {
  job_type: 'Job type',
  schedule: 'schedule',
  staffing: 'staffing',
  invites: 'invites',
  instructions: 'instructions',
  pay_rate: 'pay rate',
  summary: 'summary',
};

// step_name labels for order_creation_step_completed. Intentionally pinned to
// the pre-GRAVY-9996 values pending GRAVY-10183's decision on the correct
// step_completed taxonomy — do NOT "fix" these to match STEP_VIEWED_NAME_LABEL
// here; that change belongs to GRAVY-10183.
const STEP_COMPLETED_NAME_LABEL: Record<OrderCreationStepKey, string> = {
  job_type: 'job_basics',
  schedule: 'schedule',
  staffing: 'staffing',
  invites: 'staffing', // _3b shares step 3's label on step_completed (pre-9996)
  instructions: 'instructions',
  pay_rate: 'pay_rate',
  summary: 'summary',
};

const STEP_VIEWED_INDEX: Record<OrderCreationStepKey, number> = {
  job_type: 1,
  schedule: 2,
  staffing: 3,
  invites: 3, // _3b shares the index of step 3 per the spec
  instructions: 4,
  pay_rate: 5,
  summary: 6,
};

export function trackOrderCreationStepViewed(args: {
  step: OrderCreationStepKey;
  time_since_flow_start_sec: number;
}): void {
  track(STEP_VIEWED_EVENT_NAME[args.step], {
    step_name: STEP_VIEWED_NAME_LABEL[args.step],
    step_index: STEP_VIEWED_INDEX[args.step],
    time_since_flow_start_sec: args.time_since_flow_start_sec,
  });
}

// Staffing-step `step_completed` carries extra aggregate counters per the
// worker-actions spec. Optional everywhere else (the wizard only knows
// these on step 3, and other steps shouldn't pollute the payload).
export type StaffingStepAggregates = {
  total_invited: number;
  total_hired: number;
  overtime_warnings_shown: number;
  overtime_warnings_accepted: number;
  overtime_warnings_dismissed: number;
};

export function trackOrderCreationStepCompleted(args: {
  step: OrderCreationStepKey;
  time_on_step_sec: number;
  staffing_aggregates?: StaffingStepAggregates;
}): void {
  const props: Record<string, unknown> = {
    step_name: STEP_COMPLETED_NAME_LABEL[args.step],
    step_index: STEP_VIEWED_INDEX[args.step],
    time_on_step_sec: args.time_on_step_sec,
  };
  if (args.staffing_aggregates) {
    Object.assign(props, args.staffing_aggregates);
  }
  track('order_creation_step_completed', props);
}

export type PayRateInputMethod = 'slider' | 'typed' | 'stepper';

export function trackOrderPayRateInteracted(args: {
  rate_entered: number; // dollars
  suggested_rate: number | null; // dollars, or null when no range (STATIC)
  delta_from_suggestion: number | null;
  input_method: PayRateInputMethod;
}): void {
  orderFlowPayRateEditCount += 1;
  track('order_pay_rate_interacted', {
    rate_entered: args.rate_entered,
    suggested_rate: args.suggested_rate,
    delta_from_suggestion: args.delta_from_suggestion,
    input_method: args.input_method,
    edit_count: orderFlowPayRateEditCount,
  });
}

export type ShiftType = 'public' | 'invited';

export function trackOrderSubmitted(args: {
  order_id: string | number;
  shifts_required: number;
  pay_rate: number | null;
  shift_type: ShiftType | null;
  region: string | null;
  time_to_create_mins: number;
  total_value: number | null; // pay_rate * shifts_required * avg_hours
}): void {
  // `pay_rate`, `shift_type`, `total_value` are nullable because the
  // wizard's first-pass wiring doesn't have them readily at submit time.
  // Plumb proper values when the data layer surfaces them (per-job rates,
  // staffing-strategy → shift_type mapping, derived total) so the revenue
  // funnel reports correctly.
  track('order_submitted', {
    order_id: toIdString(args.order_id),
    shifts_required: args.shifts_required,
    pay_rate: args.pay_rate,
    shift_type: args.shift_type,
    region: args.region,
    time_to_create_mins: args.time_to_create_mins,
    total_value: args.total_value,
  });
}

export function trackOrderPayRateIncreased(args: {
  order_id: string | number;
  old_pay_rate: number;
  new_pay_rate: number;
  reason: string | null;
  hours_until_shift: number | null;
  current_fill_count: number | null;
}): void {
  track('order_pay_rate_increased', {
    order_id: toIdString(args.order_id),
    old_pay_rate: args.old_pay_rate,
    new_pay_rate: args.new_pay_rate,
    reason: args.reason,
    hours_until_shift: args.hours_until_shift,
    current_fill_count: args.current_fill_count,
  });
}

export type OrderCancelReason =
  | 'overstaffed'
  | 'event_cancelled'
  | 'budget'
  | 'other';

export function trackOrderCancelled(args: {
  order_id: string | number;
  cancel_reason: OrderCancelReason;
  shifts_filled_at_cancel: number | null;
  pay_rate: number | null;
  hours_before_shift: number | null;
}): void {
  // `shifts_filled_at_cancel` / `pay_rate` / `hours_before_shift` are
  // nullable until the order screen plumbs those derivations into the
  // cancel handler. The CSV spec expects numbers; send what we have.
  track('order_cancelled', {
    order_id: toIdString(args.order_id),
    cancel_reason: args.cancel_reason,
    shifts_filled_at_cancel: args.shifts_filled_at_cancel,
    pay_rate: args.pay_rate,
    hours_before_shift: args.hours_before_shift,
  });
}

export function trackOrderReposted(args: {
  original_order_id: string | number;
  new_order_id: string | number;
  // Nullable: tenant-admin template payload doesn't expose the source
  // order's created_at, so the delta isn't computable client-side. Plumb
  // a number once the API surfaces it.
  time_since_original_days: number | null;
}): void {
  track('order_reposted', {
    original_order_id: toIdString(args.original_order_id),
    new_order_id: toIdString(args.new_order_id),
    time_since_original_days: args.time_since_original_days,
  });
}

// ─── Worker Management & Invitations (#13–15) ────────────────────────────

export type WorkerInviteMethod = 'direct' | 'roster' | 'recommendation';

// Per the worker-actions spec: a successful invite always carries the
// worker's prior weekly hours + whether the OT modal gated this invite +
// the OT delta. `overtime_triggered: false` means the gate ran (or didn't
// need to) and the worker is safely under 40h; `null` means the surface
// couldn't compute OT context (e.g., a stub flow without the gate hook).
export function trackWorkerInvited(args: {
  job_id: string | number;
  worker_id: string | number;
  worker_current_hours: number | null;
  overtime_triggered: boolean;
  overtime_hours: number | null;
}): void {
  track('worker_invited', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    worker_current_hours: args.worker_current_hours,
    overtime_triggered: args.overtime_triggered,
    overtime_hours: args.overtime_hours,
  });
}

// Mirrors `worker_invited` so funnel reports can compare invite vs hire
// directly. `overtime_triggered` follows the same semantics.
export function trackWorkerHired(args: {
  job_id: string | number;
  worker_id: string | number;
  worker_current_hours: number | null;
  overtime_triggered: boolean;
  overtime_hours: number | null;
}): void {
  track('worker_hired', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    worker_current_hours: args.worker_current_hours,
    overtime_triggered: args.overtime_triggered,
    overtime_hours: args.overtime_hours,
  });
}

// Fires when admin cancels an open invitation on a worker. `was_overtime`
// is set when the original invite tripped the OT gate (so dashboards can
// see whether risky invites get cancelled more often).
export function trackInviteCancelled(args: {
  job_id: string | number;
  worker_id: string | number;
  was_overtime: boolean | null;
}): void {
  track('invite_cancelled', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    was_overtime: args.was_overtime,
  });
}

// Fires when admin removes a hired worker from the shift. `was_overtime`
// flags whether the original hire was overtime-gated so dashboards can
// correlate removal rate with overtime hires.
export function trackWorkerRemoved(args: {
  job_id: string | number;
  worker_id: string | number;
  was_overtime: boolean | null;
}): void {
  track('worker_removed', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    was_overtime: args.was_overtime,
  });
}

// ─── Overtime modal (worker-actions spec) ─────────────────────────────────
//
// All three fire from inside `useOvertimeGate` so every surface that calls
// the gate (wizard staffing, NewOfferModal, AddWorkerModal,
// InviteAndHireModal, WorkerStaffingModel) gets identical instrumentation.
// `action_type` mirrors the gate's `mode` arg ('invite' | 'hire').
// `already_in_overtime` is true when the worker is already at >40h before
// the proposed shift (Case B in the modal copy).

export type OvertimeModalActionType = 'invite' | 'hire';
export type OvertimeModalDismissMethod = 'go_back' | 'close_x';

export function trackOvertimeModalShown(args: {
  job_id: string | number | null;
  worker_id: string | number;
  worker_current_hours: number;
  shift_hours: number;
  projected_total_hours: number;
  overtime_hours: number;
  action_type: OvertimeModalActionType;
  already_in_overtime: boolean;
}): void {
  track('overtime_modal_shown', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    worker_current_hours: args.worker_current_hours,
    shift_hours: args.shift_hours,
    projected_total_hours: args.projected_total_hours,
    overtime_hours: args.overtime_hours,
    action_type: args.action_type,
    already_in_overtime: args.already_in_overtime,
  });
}

export function trackOvertimeModalConfirmed(args: {
  job_id: string | number | null;
  worker_id: string | number;
  overtime_hours: number;
  action_type: OvertimeModalActionType;
  already_in_overtime: boolean;
  // false when the admin confirmed the fail-closed "projection unavailable"
  // warning rather than a real over-40 projection. Defaults to true.
  projection_available?: boolean;
}): void {
  track('overtime_modal_confirmed', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    overtime_hours: args.overtime_hours,
    action_type: args.action_type,
    already_in_overtime: args.already_in_overtime,
    projection_available: args.projection_available ?? true,
  });
}

export function trackOvertimeModalDismissed(args: {
  job_id: string | number | null;
  worker_id: string | number;
  overtime_hours: number;
  dismiss_method: OvertimeModalDismissMethod;
  already_in_overtime: boolean;
  // false when the dismissed modal was the fail-closed "projection
  // unavailable" warning. Defaults to true.
  projection_available?: boolean;
}): void {
  track('overtime_modal_dismissed', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    overtime_hours: args.overtime_hours,
    dismiss_method: args.dismiss_method,
    already_in_overtime: args.already_in_overtime,
    projection_available: args.projection_available ?? true,
  });
}

// Fires when the OT gate can't compute a projection (the request failed or
// was blocked) and falls back to the conservative "Overtime Check
// Unavailable" confirmation. Distinct from `overtime_modal_shown` so
// analytics can measure how often admins hit — and override — the degraded
// gate, a signal of backend/projection reliability problems.
export function trackOvertimeProjectionUnavailable(args: {
  job_id: string | number | null;
  worker_id: string | number;
  action_type: OvertimeModalActionType;
}): void {
  track('overtime_projection_unavailable', {
    job_id: toIdString(args.job_id),
    worker_id: toIdString(args.worker_id),
    action_type: args.action_type,
  });
}

export type WorkerFavoriteReason = 'post_shift' | 'manual';

export function trackWorkerFavorited(args: {
  worker_id: string | number;
  shift_id: string | number | null;
  client_id: string | number;
  reason: WorkerFavoriteReason;
}): void {
  track('worker_favorited', {
    worker_id: toIdString(args.worker_id),
    shift_id: toIdString(args.shift_id),
    client_id: toIdString(args.client_id),
    reason: args.reason,
  });
}

export function trackWorkerRated(args: {
  worker_id: string | number;
  shift_id: string | number;
  order_id: string | number;
  rating_value: number; // 1–5
  comment_given: boolean;
}): void {
  track('worker_rated', {
    worker_id: toIdString(args.worker_id),
    shift_id: toIdString(args.shift_id),
    order_id: toIdString(args.order_id),
    rating_value: args.rating_value,
    comment_given: args.comment_given,
  });
}

// ─── Billing & Payments (#16–18) ─────────────────────────────────────────

export type PaymentType = 'card' | 'ach' | 'invoice';
export type BillingTier = 'prepay' | 'net30';

export function trackPaymentMethodAdded(args: {
  payment_type: PaymentType;
  is_first_payment_method: boolean;
  billing_tier: BillingTier | null;
}): void {
  track('payment_method_added', {
    payment_type: args.payment_type,
    is_first_payment_method: args.is_first_payment_method,
    billing_tier: args.billing_tier,
  });
}

export function trackInvoiceViewed(args: {
  invoice_id: string | number;
  invoice_amount: number;
  days_overdue: number; // 0 = current
  order_id: string | number | null;
}): void {
  track('invoice_viewed', {
    invoice_id: toIdString(args.invoice_id),
    invoice_amount: args.invoice_amount,
    days_overdue: args.days_overdue,
    order_id: toIdString(args.order_id),
  });
}

export function trackPaymentCompleted(args: {
  invoice_id: string | number;
  payment_amount: number;
  payment_method: PaymentType;
  order_id: string | number | null;
}): void {
  track('payment_completed', {
    invoice_id: toIdString(args.invoice_id),
    payment_amount: args.payment_amount,
    payment_method: args.payment_method,
    order_id: toIdString(args.order_id),
  });
}

// ─── Support & Satisfaction (#19–20) ─────────────────────────────────────

export type SupportTicketCategory =
  | 'no_show'
  | 'billing'
  | 'quality'
  | 'app_bug';
export type SupportTicketChannel = 'in_app' | 'email';

export function trackSupportTicketCreated(args: {
  ticket_category: SupportTicketCategory;
  related_order_id: string | number | null;
  priority: string;
  channel: SupportTicketChannel;
}): void {
  track('support_ticket_created', {
    ticket_category: args.ticket_category,
    related_order_id: toIdString(args.related_order_id),
    priority: args.priority,
    channel: args.channel,
  });
}

export function trackNpsSurveySubmitted(args: {
  nps_score: number; // 0–10
  verbatim_length: number; // chars
  orders_completed_count: number;
  segment: string | null;
}): void {
  track('nps_survey_submitted', {
    nps_score: args.nps_score,
    verbatim_length: args.verbatim_length,
    orders_completed_count: args.orders_completed_count,
    segment: args.segment,
  });
}

// ─── Out-of-CSV legacy helpers ────────────────────────────────────────────
// `template_saved` is not in the CSV but predates this refactor. Kept for
// backward source compatibility; safe to remove once any call site lands
// (currently none in production code).

export type TemplateKind = 'order_template' | 'snippet';

export function trackTemplateSaved(args: {
  template_id: number | string | null;
  kind: TemplateKind;
}): void {
  track('template_saved', {
    template_id: toIdString(args.template_id),
    kind: args.kind,
  });
}

// ─── Dashboard V2 ─────────────────────────────────────────────────────────
//
// Dashboard V2 shipped to production on 2026-08-10 with ZERO instrumentation:
// four tabs, twenty-nine components, and not one tracked interaction. Nothing
// could answer whether the single biggest delivery item of the quarter was
// used, which parts of it earned their keep, or whether opening it changed
// what an admin did.
//
// These four events close that gap and are the source for the dashboard
// domain's rollups (graphy: Analytics::Rollup::DashboardService). The property
// names here ARE the dimension names that service groups by — `tab`,
// `feature`, `action` — so renaming one here silently splits its metric into
// two buckets on the next rollup. Keep them in step with
// Analytics::MetricRegistry::DASHBOARD.
//
// Dashboard V2 is TenantAdmin-only (enforced by `require_tenant_admin` on
// API::Admin::DashboardV2::BaseController), so every event below is
// tenant-admin traffic by construction — there is no client-admin segment to
// split out.

export type DashboardV2Tab =
  | 'calendar'
  | 'no_show'
  | 'missing_timesheet'
  | 'needs_approval';

// Interactions that reshape what the admin is looking at. Deliberately NOT
// every click: this measures whether a control is used at all, so one entry
// per control, not per value.
export type DashboardV2Feature =
  | 'filter_client'
  | 'filter_region'
  | 'filter_skill'
  | 'filter_status'
  | 'filter_reset'
  | 'week_nav'
  | 'view_toggle'
  | 'shift_detail_open'
  | 'invited_workers_open'
  | 'recent_activity_open'
  | 'queue_paginate'
  | 'ops_on_duty_open';

// Real work, as opposed to looking around. `dashboard.action_rate` is the
// share of dashboard sessions producing at least one of these, and it is the
// metric that separates a surface people act on from one they bounce off.
//
// DELIBERATELY limited to the actions Dashboard V2 can actually perform today,
// which is exactly two: hiring an invited worker (InvitedWorkersModal) and
// completing a timesheet resolution (QueueResolveTimesheet). An earlier
// version of this union listed seven, including several the surface has no
// write path for — and a value that can never be emitted makes the metric look
// broken rather than empty, which is worse than not having it.
//
// Adding a value here without adding its call site recreates that problem.
export type DashboardV2Action = 'hire_worker' | 'resolve_timesheet';

export function trackDashboardV2Viewed(args: {
  tab: DashboardV2Tab;
  view_mode?: 'day' | 'week';
}): void {
  track('dashboard_v2_viewed', {
    tab: args.tab,
    view_mode: args.view_mode ?? null,
  });
}

export function trackDashboardV2TabViewed(args: {
  tab: DashboardV2Tab;
  // Row count on arrival — a queue tab that is always empty is used
  // differently from one that is always full, and reach alone cannot tell
  // those apart.
  item_count?: number | null;
}): void {
  track('dashboard_v2_tab_viewed', {
    tab: args.tab,
    item_count: args.item_count ?? null,
  });
}

export function trackDashboardV2FeatureUsed(args: {
  feature: DashboardV2Feature;
  tab?: DashboardV2Tab;
}): void {
  track('dashboard_v2_feature_used', {
    feature: args.feature,
    tab: args.tab ?? null,
  });
}

export function trackDashboardV2ActionTaken(args: {
  action: DashboardV2Action;
  tab?: DashboardV2Tab;
  shift_id?: number | string | null;
  worker_id?: number | string | null;
}): void {
  track('dashboard_v2_action_taken', {
    action: args.action,
    tab: args.tab ?? null,
    shift_id: toIdString(args.shift_id ?? null),
    worker_id: toIdString(args.worker_id ?? null),
  });
}
